import {
  Active,
  DragEndEvent,
  DragOverEvent,
  DroppableContainer,
} from "@dnd-kit/core";

/**
 * Because of the interplay between `getNextCoordinates` and `collisionDetection` when handling keyboard interactions,
 * we store the current position in a global variable.
 *
 * More specifically, the `collisionDetection` function doesn't have access to the same context as the `getNextCoordinates` function,
 * meaning that the collision detection function doesn't know which droppable container is currently being "hovered" by key presses.
 */
export const WINDOW_PROPERTY_SORT_POSITION = "react-hammer_DndSort_position";

/**
 * Resolves the zone (if any) where the item is being dropped.
 * Given that "over" can be a zone or an item, we have to dig into the event to determine the correct zone.
 * @param event - The drag event (either DragEndEvent or DragOverEvent)
 * @returns The resolved drop zone, which may be null if no valid zone is found
 */
export function resolveDropZone(event: DragEndEvent | DragOverEvent) {
  const targetIsZone = event.over?.data.current?.isZone === true;
  const fallbackZone = event.collisions?.find(
    (collision) =>
      collision?.data?.droppableContainer.data.current.isZone === true,
  );
  const resolvedZone = targetIsZone
    ? event.over
    : (fallbackZone?.data?.droppableContainer as DroppableContainer);
  return resolvedZone;
}

/**
 * Calculates the validity of the drop event based on accepted types and custom validation.
 * @param params - An object containing the active drag item and the accepted types and validator function
 * @param params.active - The active drag item, which contains the current type and ID
 * @param params.acceptedTypes - An array of accepted types for the drop zone (optional)
 * @param params.validator - A custom validation function that takes the ID of the active item and returns a boolean
 * @returns boolean - true if valid, false otherwise
 */
export function calculateValidity({
  active,
  acceptedTypes,
  validator,
}: {
  active: Active | null;
  acceptedTypes?: string[];
  validator?: (id: string | number) => boolean;
}) {
  if (!active) return true;
  const hasTypes = !!acceptedTypes;
  const isValidType =
    !hasTypes || // If there are no accepted types, everything is valid
    (hasTypes && // If there are accepted types, check if the active item's type is included
      !!acceptedTypes?.includes(active.data.current?.type));
  const isValidCustom = validator?.(active.id) ?? true;
  return isValidType && isValidCustom;
}

/**
 * Calculates the validity of the drop event based on accepted types and custom validation.
 * @param params - An object containing the active drag item and the drop zone information
 * @param params.active - The active drag item, which contains the current type and ID
 * @param params.zone - The drop zone information, which may include accepted types and a custom validator function
 * @returns boolean - true if valid, false otherwise
 */
export function calculateZoneValidity({
  active,
  zone,
}: {
  active: Active | null;
  zone: {
    id: string | number;
    data: {
      current?: {
        acceptedTypes?: string[];
        validator?: (id: string | number) => boolean;
      };
    };
  } | null;
}) {
  return calculateValidity({
    active,
    acceptedTypes: zone?.data.current?.acceptedTypes,
    validator: zone?.data.current?.validator,
  });
}

/**
 * Determines whether the drop event is valid based on the accepted types and custom validation function.
 * Internally resolves the drop zone.
 * @param event - The drag event (either DragEndEvent or DragOverEvent)
 * @returns boolean - true if valid, false otherwise
 */
export function calculateEventValidity(event: DragEndEvent | DragOverEvent) {
  return calculateZoneValidity({
    active: event.active,
    zone: resolveDropZone(event),
  });
}
